import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import AccentColorPicker from '../components/ui/AccentColorPicker';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { getLogoUrl } from '../utils/formatters';
import PhoneInput from '../components/forms/PhoneInput';
import StateSelect from '../components/forms/StateSelect';
import { getSupportedCurrencies } from '../utils/currency';

const AdminPage: React.FC = () => {
	const queryClient = useQueryClient();
	const [companyName, setCompanyName] = useState('');
	const [companyAddress, setCompanyAddress] = useState('');
	const [companyCity, setCompanyCity] = useState('');
	const [companyState, setCompanyState] = useState('');
	const [companyZipCode, setCompanyZipCode] = useState('');
	const [companyPhone, setCompanyPhone] = useState('');
	const [companyEmail, setCompanyEmail] = useState('');
	const [companyWebsite, setCompanyWebsite] = useState('');
	const [logoUrl, setLogoUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [logoLoading, setLogoLoading] = useState(false);
	const [deleteLogoLoading, setDeleteLogoLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [isDragOver, setIsDragOver] = useState(false);
	const [showLogoModal, setShowLogoModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [sessionExpiryMinutes, setSessionExpiryMinutes] = useState<number | ''>('');
	const [sessionWarningLeadSeconds, setSessionWarningLeadSeconds] = useState<number | ''>('');
	const [selectedTimeZone, setSelectedTimeZone] = useState<string>('');
	const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
	const [timeZones, setTimeZones] = useState<any[]>([]);
	const { setAccentColor } = useTheme();

	// Load current company data and timezones
	useEffect(() => {
		const loadData = async () => {
			try {
				// Load company data
				const response = await apiService.getCompany();
				if (response.success && response.data) {
					setCompanyName(response.data.name || '');
					setCompanyAddress(response.data.address || '');
					setCompanyCity(response.data.city || '');
					setCompanyState(response.data.state || '');
					setCompanyZipCode(response.data.zipCode || '');
					setCompanyPhone(response.data.phone || '');
					setCompanyEmail(response.data.email || '');
					setCompanyWebsite(response.data.website || '');
					setLogoUrl(response.data.logoUrl || '');
					setSelectedTimeZone(response.data.timeZone || '');
					setSelectedCurrency(response.data.currencyCode || 'USD');
					if (typeof (response.data as any).sessionExpiryMinutes === 'number') {
						setSessionExpiryMinutes((response.data as any).sessionExpiryMinutes);
					}
					if (typeof (response.data as any).sessionWarningLeadSeconds === 'number') {
						setSessionWarningLeadSeconds((response.data as any).sessionWarningLeadSeconds);
					}
				}

				// Load timezones
				const timeZonesResponse = await apiService.getTimeZones();
				if (timeZonesResponse) {
					setTimeZones(timeZonesResponse);
				}
			} catch (error) {
				console.error('Error loading data:', error);
				toast.error('Failed to load settings data');
			} finally {
				setInitialLoading(false);
			}
		};

		loadData();
	}, []);

	const handleSaveChanges = async () => {
		if (!companyName.trim()) {
			toast.error('Company name is required');
			return;
		}

		if (!selectedTimeZone) {
			toast.error('Timezone is required');
			return;
		}

		setLoading(true);
		try {
			const warningSecondsNum = sessionWarningLeadSeconds === '' ? undefined : Number(sessionWarningLeadSeconds);
		const response = await apiService.updateCompany({
			name: companyName.trim(),
			address: companyAddress.trim(),
			city: companyCity.trim(),
			state: companyState.trim(),
			zipCode: companyZipCode.trim(),
			phone: companyPhone.trim(),
			email: companyEmail.trim(),
			website: companyWebsite.trim(),
			timeZone: selectedTimeZone,
			currencyCode: selectedCurrency,
			...(sessionExpiryMinutes !== '' ? { sessionExpiryMinutes: Number(sessionExpiryMinutes) } : {}),
			...(warningSecondsNum !== undefined && !Number.isNaN(warningSecondsNum) ? { sessionWarningLeadSeconds: warningSecondsNum } : {})
		});

			if (response.success) {
				toast.success('Company settings updated successfully!');
				
				// Invalidate company cache to refresh header and any consumers of company settings
				queryClient.invalidateQueries({ queryKey: ['company'] });
				
				// Refresh the theme to ensure accent color and session settings propagate
				const companyResponse = await apiService.getCompany();
				if (companyResponse.success && companyResponse.data?.accentColor) {
					setAccentColor(companyResponse.data.accentColor);
				}
				queryClient.invalidateQueries({ queryKey: ['company'] });
			} else {
				toast.error('Failed to update company settings');
			}
		} catch (error: any) {
			console.error('Error updating company:', error);
			console.error('Error response data:', error.response?.data);
			console.error('Error response status:', error.response?.status);
			const errorMessage = error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to update company settings';

			// If there are validation errors, show them
			if (error.response?.data?.errors) {
				const validationErrors = Object.entries(error.response.data.errors)
					.map(([field, errors]) => `${field}: ${(errors as string[]).join(', ')}`)
					.join('\n');
				console.error('Validation errors:', validationErrors);
				toast.error(`Validation errors:\n${validationErrors}`);
			} else {
				toast.error(errorMessage);
			}
		} finally {
			setLoading(false);
		}
	};

	const validateAndUploadFile = async (file: File) => {
		// Validate file type
		const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
			return;
		}

		// Validate file size (5MB limit)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (file.size > maxSize) {
			toast.error('File size must be less than 5MB');
			return;
		}

		setLogoLoading(true);
		try {
			const response = await apiService.uploadCompanyLogo(file);
			if (response.success && response.data) {
				setLogoUrl(response.data);
				// Invalidate company cache to refresh header logo
				queryClient.invalidateQueries({ queryKey: ['company'] });
				toast.success('Logo uploaded successfully!');
			} else {
				toast.error(response.message || 'Failed to upload logo');
			}
		} catch (error: unknown) {
			console.error('Error uploading logo:', error);
			const message =
				(error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
				'Failed to upload logo';
			toast.error(message);
		} finally {
			setLogoLoading(false);
		}
	};

	const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		await validateAndUploadFile(file);
	};

	const handleDragOver = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop = async (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);

		const files = event.dataTransfer.files;
		if (files.length > 0) {
			await validateAndUploadFile(files[0]);
		}
	};

	const handleDeleteLogo = async () => {
		setDeleteLogoLoading(true);
		try {
			const response = await apiService.deleteCompanyLogo();
			if (response.success) {
				setLogoUrl('');
				queryClient.invalidateQueries({ queryKey: ['company'] });
				toast.success('Logo deleted successfully!');
				setShowDeleteConfirm(false);
			} else {
				toast.error(response.message || 'Failed to delete logo');
			}
		} catch (error) {
			console.error('Error deleting logo:', error);
			toast.error('Failed to delete logo');
		} finally {
			setDeleteLogoLoading(false);
		}
	};

	if (initialLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent-color"></div>
			</div>
		);
	}

	return (
		<>
			<div className="space-y-6">
			{/* Page Header */}
			<div>
				<div className="flex items-center space-x-2">
					<Settings className="h-6 w-6 text-gray-900 dark:text-gray-100" />
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">System Settings</h1>
				</div>
				<p className="text-sm text-gray-500 dark:text-gray-400">Configure company information, timezone, and preferences</p>
			</div>

			{/* Company Information Card */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Company Information</div>
					<div className="card-body space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Company Name <span className="text-red-500">*</span>
								</label>
								<input
									className="input"
									placeholder="Company name"
									value={companyName}
									onChange={(e) => setCompanyName(e.target.value)}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Website
								</label>
								<input
									className="input"
									type="url"
									placeholder="https://www.company.com"
									value={companyWebsite}
									onChange={(e) => setCompanyWebsite(e.target.value)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Phone
								</label>
								<PhoneInput
									value={companyPhone}
									onChange={setCompanyPhone}
									placeholder="Enter phone number"
									className="input"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Email
								</label>
								<input
									className="input"
									type="email"
									placeholder="Email address"
									value={companyEmail}
									onChange={(e) => setCompanyEmail(e.target.value)}
								/>
							</div>
						</div>

						<div className="border-t pt-6">
							<h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">Address</h3>
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										Street Address
									</label>
									<input
										className="input"
										placeholder="Street address"
										value={companyAddress}
										onChange={(e) => setCompanyAddress(e.target.value)}
										autoComplete="street-address"
									/>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											City
										</label>
										<input
											className="input"
											placeholder="City"
											value={companyCity}
											onChange={(e) => setCompanyCity(e.target.value)}
											autoComplete="address-level2"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											State
										</label>
										<StateSelect
											value={companyState}
											onChange={(value) => setCompanyState(value)}
											placeholder="Select state"
											autoComplete="address-level1"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											Zip Code
										</label>
										<input
											className="input"
											placeholder="Zip code"
											value={companyZipCode}
											onChange={(e) => setCompanyZipCode(e.target.value)}
											autoComplete="postal-code"
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="border-t pt-6">
							<h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">Company Logo</h3>
							<div className="space-y-3">
								{logoUrl && (
									<div className="flex items-center space-x-3">
										<img
											src={getLogoUrl(logoUrl)}
											alt="Company Logo"
											className="h-16 w-16 object-contain border border-gray-200 rounded-md cursor-pointer hover:opacity-80 transition-opacity"
											onClick={() => setShowLogoModal(true)}
											title="Click to view larger"
										/>
										<div className="flex-1 text-sm text-gray-600 dark:text-gray-400">
											<p>Current logo</p>
											<p className="text-xs text-gray-500">Click to view larger • Drag & drop or click to replace</p>
										</div>
										<button
											type="button"
											onClick={() => setShowDeleteConfirm(true)}
											disabled={deleteLogoLoading}
											className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="Delete logo"
										>
											<Trash2 className="h-4 w-4" />
											<span>Delete</span>
										</button>
									</div>
								)}

								<div
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
									onDrop={handleDrop}
									className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
										isDragOver
											? 'border-accent-color bg-accent-color-50 dark:bg-accent-color-900'
											: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
									} ${logoLoading ? 'opacity-50 pointer-events-none' : ''}`}
								>
									<input
										type="file"
										accept="image/jpeg,image/jpg,image/png,image/gif"
										onChange={handleLogoUpload}
										disabled={logoLoading}
										className="hidden"
										id="logo-upload"
									/>
									<div className="space-y-2">
										<div className="mx-auto w-12 h-12 text-gray-400">
											{logoLoading ? (
												<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color"></div>
											) : (
												<svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
												</svg>
											)}
										</div>
										<div className="text-sm">
											{logoLoading ? (
												<p className="text-accent-color font-medium">Uploading...</p>
											) : (
												<>
													<p className="text-gray-600 dark:text-gray-400">
														<span className="font-medium text-accent-color">Click to upload</span> or drag and drop
													</p>
													<p className="text-xs text-gray-500 mt-1">
														JPEG, PNG, GIF up to 5MB
													</p>
												</>
											)}
										</div>
										<label
											htmlFor="logo-upload"
											className="btn-primary cursor-pointer"
										>
											{logoUrl ? 'Choose New Logo' : 'Choose Logo'}
										</label>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Timezone & Regional Settings Card */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Timezone & Regional Settings</div>
					<div className="card-body space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Company Timezone <span className="text-red-500">*</span>
								</label>
								<select
									value={selectedTimeZone}
									onChange={(e) => setSelectedTimeZone(e.target.value)}
									className="input"
								>
									<option value="">Select a timezone...</option>
									{timeZones.map((tz) => (
										<option key={tz.id} value={tz.id}>
											{tz.displayName}
										</option>
									))}
								</select>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All times in the system will be displayed in this timezone.</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Default Currency <span className="text-red-500">*</span>
								</label>
								<select
									value={selectedCurrency}
									onChange={(e) => setSelectedCurrency(e.target.value)}
									className="input"
								>
									{getSupportedCurrencies().map((currency) => (
										<option key={currency.value} value={currency.value}>
											{currency.label}
										</option>
									))}
								</select>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Default currency for invoices and payments.</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Session Settings Card */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Session Settings</div>
					<div className="card-body space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Session Expiry (minutes)</label>
				<input
					className="input"
					type="number"
					min={1}
					inputMode="numeric"
					pattern="\\d*"
					placeholder="e.g., 480 (8 hours)"
					value={sessionExpiryMinutes}
					onChange={(e) => {
						const raw = e.target.value;
						const digitsOnly = raw.replace(/[^0-9]/g, '');
						if (digitsOnly === '') {
							setSessionExpiryMinutes('');
							return;
						}
						const num = Math.max(1, Number(digitsOnly));
						setSessionExpiryMinutes(Number.isNaN(num) ? '' : num);
					}}
				/>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">How long before user sessions expire</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Warning Lead Time (seconds)</label>
				<input
					className="input"
					type="number"
					min={0}
					inputMode="numeric"
					pattern="\\d*"
					placeholder="e.g., 60"
					value={sessionWarningLeadSeconds}
					onChange={(e) => {
						const raw = e.target.value;
						const digitsOnly = raw.replace(/[^0-9]/g, '');
						if (digitsOnly === '') {
							setSessionWarningLeadSeconds('');
							return;
						}
						const num = Math.max(0, Number(digitsOnly));
						setSessionWarningLeadSeconds(Number.isNaN(num) ? '' : num);
					}}
				/>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">When to warn users before session expires</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Workflow Notifications */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Workflow Notifications</div>
					<div className="card-body flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Configure who receives completion reports, Ready to Invoice alerts, NTE exceeded notices, and client approval updates.
						</p>
						<Link to="/admin/workflow-notifications" className="btn-primary whitespace-nowrap">
							Manage Recipients
						</Link>
					</div>
				</div>
			</div>

			{/* Email Integration */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Email Integration</div>
					<div className="card-body flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Connect the Gmail inbox that receives work order emails from your clients and vendors. Unread mail from your allowed sender domains is imported automatically.
						</p>
						<Link to="/admin/email-integration" className="btn-primary whitespace-nowrap">
							Configure Gmail
						</Link>
					</div>
				</div>
			</div>

			{/* Theme Settings Card */}
			<div className="max-w-4xl">
				<div className="card">
					<div className="card-header">Theme Settings</div>
					<div className="card-body">
						<AccentColorPicker />
					</div>
				</div>
			</div>

			{/* Save Button */}
			<div className="max-w-4xl">
				<div className="flex justify-end">
					<button
						className="btn-primary"
						onClick={handleSaveChanges}
						disabled={loading}
					>
						{loading ? 'Saving...' : 'Save All Changes'}
					</button>
				</div>
			</div>
		</div>

		{/* Logo Preview Modal */}
		<Modal
			isOpen={showLogoModal}
			onClose={() => setShowLogoModal(false)}
			title="Company Logo"
			maxWidth="2xl"
		>
			<div className="space-y-4">
				<div className="flex justify-center">
					<img 
						src={getLogoUrl(logoUrl)} 
						alt="Company Logo" 
						className="max-w-full max-h-[70vh] object-contain"
					/>
				</div>
				<div className="text-center pt-4 border-t border-gray-200">
					<button
						onClick={() => setShowLogoModal(false)}
						className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
					>
						Close
					</button>
				</div>
			</div>
		</Modal>

		{/* Delete Logo Confirmation Dialog */}
		<ConfirmDialog
			isOpen={showDeleteConfirm}
			onClose={() => setShowDeleteConfirm(false)}
			onConfirm={handleDeleteLogo}
			title="Delete Company Logo"
			message="Are you sure you want to delete the company logo? This action cannot be undone and the file will be removed from the file system."
			confirmText="Delete Logo"
			cancelText="Cancel"
			variant="danger"
			isLoading={deleteLogoLoading}
		/>
		</>
	);
};

export default AdminPage;
